import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, DollarSign, User, FileText, Award } from "lucide-react";
import { format, parseISO } from "date-fns";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  hire_date: string;
  created_at: string;
}

interface TimeEntry {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
}

interface PayrollCalculation {
  id: string;
  period_start: string;
  period_end: string;
  gross_pay: number;
  status: string;
  created_at: string;
}

interface EmployeeTimelineProps {
  employee: Employee;
  timeEntries: TimeEntry[];
  payrollHistory: PayrollCalculation[];
}

interface TimelineEvent {
  id: string;
  date: string;
  type: 'hire' | 'timeEntry' | 'payroll' | 'milestone';
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

export const EmployeeTimeline: React.FC<EmployeeTimelineProps> = ({
  employee,
  timeEntries,
  payrollHistory
}) => {
  // Generate timeline events
  const generateTimelineEvents = (): TimelineEvent[] => {
    const events: TimelineEvent[] = [];

    // Add hire date
    events.push({
      id: 'hire',
      date: employee.hire_date,
      type: 'hire',
      title: 'Employee Hired',
      description: `${employee.first_name} ${employee.last_name} joined the company`,
      icon: <User className="h-4 w-4" />,
      color: 'bg-green-500'
    });

    // Add recent time entries (last 10)
    timeEntries.slice(0, 10).forEach((entry, index) => {
      const hours = entry.total_hours || 0;
      events.push({
        id: `time-${entry.id}`,
        date: entry.clock_in_time,
        type: 'timeEntry',
        title: 'Work Shift',
        description: `Worked ${hours.toFixed(1)} hours${entry.clock_out_time ? '' : ' (still active)'}`,
        icon: <Clock className="h-4 w-4" />,
        color: entry.clock_out_time ? 'bg-blue-500' : 'bg-orange-500'
      });
    });

    // Add payroll events
    payrollHistory.forEach((payroll) => {
      events.push({
        id: `payroll-${payroll.id}`,
        date: payroll.created_at,
        type: 'payroll',
        title: 'Payroll Processed',
        description: `Pay period: ${format(parseISO(payroll.period_start), 'MMM dd')} - ${format(parseISO(payroll.period_end), 'MMM dd')} • $${payroll.gross_pay.toFixed(2)}`,
        icon: <DollarSign className="h-4 w-4" />,
        color: payroll.status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'
      });
    });

    // Add milestones
    const hireDate = parseISO(employee.hire_date);
    const now = new Date();
    const monthsEmployed = Math.floor((now.getTime() - hireDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
    
    // Add milestone events for significant periods
    if (monthsEmployed >= 12) {
      const anniversaryDate = new Date(hireDate);
      anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);
      events.push({
        id: 'anniversary-1',
        date: anniversaryDate.toISOString(),
        type: 'milestone',
        title: '1 Year Anniversary',
        description: 'Completed first year with the company',
        icon: <Award className="h-4 w-4" />,
        color: 'bg-purple-500'
      });
    }

    if (monthsEmployed >= 6) {
      const sixMonthDate = new Date(hireDate);
      sixMonthDate.setMonth(sixMonthDate.getMonth() + 6);
      events.push({
        id: 'milestone-6months',
        date: sixMonthDate.toISOString(),
        type: 'milestone',
        title: '6 Month Milestone',
        description: 'Completed probationary period',
        icon: <Award className="h-4 w-4" />,
        color: 'bg-indigo-500'
      });
    }

    // Sort events by date (most recent first)
    return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const timelineEvents = generateTimelineEvents();

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'hire':
        return 'bg-green-100 text-green-800';
      case 'timeEntry':
        return 'bg-blue-100 text-blue-800';
      case 'payroll':
        return 'bg-green-100 text-green-800';
      case 'milestone':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Timeline Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Employment Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold">
                {Math.floor((Date.now() - new Date(employee.hire_date).getTime()) / (1000 * 60 * 60 * 24))}
              </p>
              <p className="text-sm text-muted-foreground">Days Employed</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{timeEntries.length}</p>
              <p className="text-sm text-muted-foreground">Recent Shifts</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{payrollHistory.length}</p>
              <p className="text-sm text-muted-foreground">Pay Periods</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline Events */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {timelineEvents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No timeline events found</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
              
              {/* Timeline events */}
              <div className="space-y-6">
                {timelineEvents.map((event, index) => (
                  <div key={event.id} className="relative flex items-start gap-4">
                    {/* Timeline dot */}
                    <div className={`flex items-center justify-center w-12 h-12 rounded-full ${event.color} text-white z-10`}>
                      {event.icon}
                    </div>
                    
                    {/* Event content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{event.title}</h4>
                        <Badge className={getEventTypeColor(event.type)} variant="secondary">
                          {event.type.replace(/([A-Z])/g, ' $1').trim()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(parseISO(event.date), 'MMMM dd, yyyy • h:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};